// Step 25 — Command Center domain-summary tiles.
//
// Renders deterministic COUNTERS from existing endpoints. Failed
// source → tile shows "—" (never 0), with a small "Källa ej
// tillgänglig" hint. No synthetic overall score, no closing
// probability, no health index.
//
// Each domain tile links into the specialized workspace that owns
// the underlying source (Step 22 buyers, Step 23 contracts, Step 24
// execution, existing actions/risks/obligations/activity pages).

import { Link } from "react-router-dom";

import { AISection } from "../AIPrimitives";
import { commandCounts, COMMAND_SECTION } from "./helpers";

function _cell(value, hint) {
  return (
    <div className="qai-command-count-cell">
      <div className="qai-command-count-value">
        {value == null ? "—" : String(value)}
      </div>
      {hint && <div className="qai-command-count-hint">{hint}</div>}
    </div>
  );
}

export default function AICommandCounts({
  actions,
  contracts,
  situation,
  snapshots,
  notifications,
  workspaceId,
  errors = {},
  now,
}) {
  const counts = commandCounts({
    actions: errors.actions ? null : actions,
    contracts: errors.contracts ? null : contracts,
    situation: errors.situation ? null : situation,
    activityCount:
      errors.snapshots || errors.notifications
        ? null
        : (snapshots?.length ?? 0) + (notifications?.length ?? 0),
    now,
  });

  const _link = (to, label) => (
    <Link to={`/deals/${workspaceId}${to}`} className="qai-page-section-link">
      {label} →
    </Link>
  );

  return (
    <AISection
      title="Domänsammanställning"
      right={
        <span className="qai-page-section-hint">
          Deterministiska räknare från befintliga endpoints
        </span>
      }
    >
      <div className="qai-command-counts">
        <div className="qai-command-count-card" data-domain="actions">
          <div className="qai-command-count-title">{COMMAND_SECTION.ACTIONS}</div>
          <div className="qai-command-count-grid">
            {_cell(counts.actions_open,
              errors.actions ? "Källa ej tillgänglig" : "Öppna")}
            {_cell(counts.actions_blocked, "Blockerade")}
            {_cell(counts.actions_overdue, "Försenade")}
            {_cell(counts.actions_pending_review, "Väntar granskning")}
            {_cell(counts.actions_completed, "Klara")}
          </div>
          <div className="qai-command-count-links">
            {_link("/actions", "Öppna åtgärder")}
            {_link("/execution", "Öppna genomförande")}
          </div>
        </div>

        <div className="qai-command-count-card" data-domain="contracts">
          <div className="qai-command-count-title">{COMMAND_SECTION.CONTRACTS}</div>
          <div className="qai-command-count-grid">
            {_cell(counts.contracts_total,
              errors.contracts ? "Källa ej tillgänglig" : "Totalt")}
            {_cell(counts.contracts_pending_review, "Väntar granskning")}
            {_cell(counts.contracts_with_uncertainties, "Med osäkerheter")}
            {_cell(counts.contracts_requires_attention, "Kräver åtgärd")}
          </div>
          <div className="qai-command-count-links">
            {_link("/contracts", "Öppna avtalsintelligence")}
            {_link("/contracts/review", "Öppna granskningsvy")}
          </div>
        </div>

        <div className="qai-command-count-card" data-domain="obligations">
          <div className="qai-command-count-title">{COMMAND_SECTION.OBLIGATIONS}</div>
          <div className="qai-command-count-grid">
            {_cell(counts.preparedness_gaps,
              errors.situation ? "Källa ej tillgänglig" : "Beredskapsgap")}
          </div>
          <div className="qai-command-count-hint">
            Backend saknar egen obligation-modell — visar beredskaps-
            observationer från deal_situation som det närmaste
            deterministiska signalet.
          </div>
        </div>

        <div className="qai-command-count-card" data-domain="risks">
          <div className="qai-command-count-title">{COMMAND_SECTION.RISKS}</div>
          <div className="qai-command-count-grid">
            {_cell(counts.risks_total,
              errors.situation ? "Källa ej tillgänglig" : "Registrerade")}
            {_cell(counts.risks_high, "Hög")}
          </div>
          <div className="qai-command-count-links">
            {_link("/risks", "Öppna risker")}
          </div>
        </div>

        <div className="qai-command-count-card" data-domain="buyers">
          <div className="qai-command-count-title">{COMMAND_SECTION.BUYERS}</div>
          <div className="qai-command-count-grid">
            {_cell(counts.buyers_total,
              errors.situation ? "Källa ej tillgänglig" : "Köpare")}
            {_cell(counts.buyers_silent, "Tysta")}
          </div>
          <div className="qai-command-count-links">
            {_link("/buyers", "Öppna köparjämförelse")}
          </div>
        </div>

        <div className="qai-command-count-card" data-domain="activity">
          <div className="qai-command-count-title">Aktivitet</div>
          <div className="qai-command-count-grid">
            {_cell(counts.activity_recent,
              errors.snapshots || errors.notifications ? "Källa ej tillgänglig" : "Senaste poster")}
          </div>
          <div className="qai-command-count-links">
            {_link("/activity", "Öppna aktivitet")}
          </div>
        </div>
      </div>
    </AISection>
  );
}
